import { validMapCoordinates, type MapCoordinates } from '../../shared/results_page/results_map.js';
import type { RentalOfferView } from '../models.js';
import type { RentalFilterableOffer } from './rental_results_filters.js';

/** O que o mapa precisa de uma oferta: a view (onde retira, de quem) e o total
 *  numérico do período — o mesmo número que ordena por preço. */
export type RentalMapOffer = Pick<RentalFilterableOffer, 'offer' | 'totalPrice'>;

/** Um pino do mapa do carro: uma AGÊNCIA de retirada e as ofertas que saem
 *  dela. Várias ofertas da mesma locadora compartilham a agência, então o
 *  agrupamento é por lugar, não por oferta — um pino por carro empilharia
 *  dez pílulas iguais no mesmo ponto. */
export type RentalAgencyPin = {
  /** A coordenada arredondada — estável entre renders e entre ofertas. É a
   *  chave do filtro do pino (`rentalAgencyKey`). */
  key: string;
  coordinates: MapCoordinates;
  /** A locadora como a primeira oferta do grupo a chama: balcões de locadoras
   *  diferentes na mesma coordenada são um pino só (o agrupador é a #3995). */
  company: string;
  /** O menor total entre as ofertas daqui. Ausente quando nenhuma tem preço. */
  fromPrice?: number;
  /** As ofertas que saem daqui, na ordem em que a lista as mostra. */
  offerIds: readonly string[];
};

/** Seis casas decimais são ~10cm: duas agências que o fornecedor cadastra com
 *  a mesma coordenada são o mesmo pino; um ruído na sétima casa não separa. */
function pinKey({ latitude, longitude }: MapCoordinates): string {
  return `${latitude.toFixed(6)}:${longitude.toFixed(6)}`;
}

/** A chave do pino da agência de retirada da oferta — `undefined` sem
 *  coordenada válida (a oferta não tem pino). */
export function rentalAgencyKey(offer: RentalOfferView): string | undefined {
  const coordinates = validMapCoordinates(offer.pickup.coordinates);
  return coordinates ? pinKey(coordinates) : undefined;
}

function lower(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

/** Os pinos das ofertas EM CENA (já cortadas e ordenadas pela página), um por
 *  agência de retirada com coordenada válida (`validMapCoordinates`) — a
 *  devolução não entra: o mapa responde "onde pego o carro". */
export function rentalAgencyPins(offers: readonly RentalMapOffer[]): RentalAgencyPin[] {
  const groups = new Map<string, RentalAgencyPin & { offerIds: string[] }>();
  for (const { offer, totalPrice } of offers) {
    const coordinates = validMapCoordinates(offer.pickup.coordinates);
    if (!coordinates) continue;
    const key = pinKey(coordinates);
    const group = groups.get(key);
    if (group) {
      group.offerIds.push(offer.id);
      const fromPrice = lower(group.fromPrice, totalPrice);
      if (fromPrice !== undefined) group.fromPrice = fromPrice;
    } else {
      groups.set(key, {
        key,
        coordinates,
        company: offer.company.name,
        ...(totalPrice === undefined ? {} : { fromPrice: totalPrice }),
        offerIds: [offer.id],
      });
    }
  }
  return [...groups.values()];
}
